import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import useWindowSize from '../../../../hooks/useWindowSize';
import type { TransitionController } from '../../../../util/core-transition-component';

export default function useStickyNav(
  elementRef: RefObject<HTMLElement | null>,
  transitionController: TransitionController,
): {
  setOffsetY: Dispatch<SetStateAction<number>>;
} {
  const [yOffset, setOffsetY] = useState(0);
  const headerHeightRef = useRef<number>(0);
  const yOffsetRef = useRef<number>(0);
  const isScrollingBack = useRef<boolean>(false);
  const isSticky = useRef<boolean>(false);
  const isHiddenOnScroll = useRef<boolean>(false);
  const makeHiddenOnScroll = useRef<boolean>(false);
  const windowSize = useWindowSize();

  const setHeaderHeight = useCallback(() => {
    headerHeightRef.current = elementRef?.current?.offsetHeight as number;
  }, [elementRef]);

  useEffect(() => {
    setHeaderHeight();
  }, [windowSize, setHeaderHeight]);

  useEffect(() => {
    (async () => {
      const value = Math.min(yOffset, headerHeightRef.current);
      isScrollingBack.current = yOffset < yOffsetRef.current;
      const isBeyondHeader = yOffset > headerHeightRef?.current;

      if (isScrollingBack.current) {
        if (isBeyondHeader && !isSticky.current) {
          isSticky.current = true;
          makeHiddenOnScroll.current = false;
          isHiddenOnScroll.current = false;
          transitionController.transitionIn();
          gsap.set(elementRef.current, { y: 0 });
        } else if (!isSticky.current) {
          gsap.set(elementRef.current, { y: -value });
        }

        if (value === 0) {
          isSticky.current = false;
        }
      } else {
        if (isSticky.current && !makeHiddenOnScroll.current) {
          makeHiddenOnScroll.current = true;
          await transitionController.transitionOut();
          isHiddenOnScroll.current = true;
        } else if (!isSticky.current) {
          if (!makeHiddenOnScroll.current) {
            gsap.set(elementRef.current, { y: -value });
          }
        }

        isSticky.current = false;
      }

      yOffsetRef.current = yOffset;
    })();
  }, [yOffset, elementRef, transitionController]);

  return {
    setOffsetY,
  };
}
